import classNames from 'classnames'
import {
  Check,
  ChevronsUpDown,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'

import { HubspotCompany } from '@/api/organization-hubspot-client'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Icons } from '@/components/ui/icons'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  useQueryGetHubspotCompanies,
  useQueryGetHubspotCompanyById
} from '@/services/hubspot.service'

// Get HubSpot portal ID from environment config
const getHubspotPortalId = () =>
  globalThis.EnvConfig?.env?.HUBSPOT_PORTAL_ID || '25127966'

const getHubspotCompanyUrl = (companyId: string) =>
  `https://app.hubspot.com/contacts/${getHubspotPortalId()}/company/${companyId}`

// Custom debounce hook to avoid external dependency
const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

type OrganizationHubspotCompanyProps = {
  loading?: boolean
  disabled?: boolean
  defaultOpen?: boolean
  selectedCompanyId?: string
  onCompanySelect: (companyId: string | undefined) => void
  /** false renders the picker without the collapsible frame, for use inside a section */
  collapsible?: boolean
}

const OrganizationHubspotCompany = ({
  loading = false,
  disabled = false,
  defaultOpen = false,
  collapsible = true,
  selectedCompanyId,
  onCompanySelect
}: OrganizationHubspotCompanyProps) => {
  const [open, setOpen] = useState(defaultOpen)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedSearch = useDebouncedValue(searchTerm, 300)

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  const { data: companiesData, isLoading: isLoadingCompanies } =
    useQueryGetHubspotCompanies(debouncedSearch, 50, {
      enabled: popoverOpen
    })

  const { data: selectedCompany, isLoading: isLoadingSelectedCompany } =
    useQueryGetHubspotCompanyById(selectedCompanyId)

  const companies = useMemo(() => {
    const results = companiesData?.results ?? []
    // Deduplicate by company_id in case the API returns duplicates
    const seen = new Set<string>()

    return results.filter((company) => {
      if (seen.has(company.company_id)) {
        return false
      }
      seen.add(company.company_id)

      return true
    })
  }, [companiesData])

  const isLoading = loading || isLoadingCompanies || isLoadingSelectedCompany

  const content = (
    <div className="max-h-[90vh] overflow-y-auto">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Linked HubSpot Company</Label>
          <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
            <PopoverTrigger asChild>
              <Button
                aria-expanded={popoverOpen}
                className={classNames('w-full justify-between', {
                  'text-primary': selectedCompanyId,
                  'text-placeholder': !selectedCompanyId
                })}
                disabled={disabled || isLoading}
                role="combobox"
                variant="outline"
              >
                {isLoadingSelectedCompany ? (
                  <Icons.spinner className="w-4 h-4 mr-2" />
                ) : selectedCompany ? (
                  <span className="truncate">
                    {selectedCompany.company_name || 'Unknown Company'}
                    {selectedCompany.domain && (
                      <span className="text-muted-foreground ml-2">
                        ({selectedCompany.domain})
                      </span>
                    )}
                  </span>
                ) : (
                  'Select a HubSpot company...'
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] p-0"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  className="h-9"
                  onValueChange={handleSearchChange}
                  placeholder="Search companies..."
                  value={searchTerm}
                />
                <CommandList>
                  {isLoadingCompanies ? (
                    <div className="flex items-center justify-center py-6">
                      <Icons.spinner className="w-4 h-4" />
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No company found.</CommandEmpty>
                      <CommandGroup>
                        {selectedCompanyId && (
                          <CommandItem
                            className="flex items-center justify-between py-2 px-4 text-muted-foreground"
                            onSelect={() => {
                              onCompanySelect(undefined)
                              setPopoverOpen(false)
                            }}
                            value="__clear__"
                          >
                            <span className="italic">Clear selection</span>
                          </CommandItem>
                        )}
                        {companies.map((company: HubspotCompany) => (
                          <CommandItem
                            className="flex items-center justify-between py-2 px-4"
                            key={company.company_id}
                            onSelect={() => {
                              onCompanySelect(company.company_id)
                              setPopoverOpen(false)
                            }}
                            value={`${company.company_name ?? ''} ${
                              company.domain ?? ''
                            } ${company.company_id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Check
                                className={classNames(
                                  'mr-2 h-4 w-4',
                                  selectedCompanyId === company.company_id
                                    ? 'opacity-100'
                                    : 'opacity-0'
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {company.company_name ?? 'Unknown'}
                                </span>
                                {company.domain && (
                                  <span className="text-xs text-muted-foreground">
                                    {company.domain}
                                  </span>
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {selectedCompanyId && selectedCompany && (
          <div className="space-y-2">
            <Label>HubSpot Link</Label>
            <div className="flex items-center gap-2">
              <a
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                href={getHubspotCompanyUrl(selectedCompanyId)}
                rel="noopener noreferrer"
                target="_blank"
              >
                Open in HubSpot
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  if (!collapsible) return content

  return (
    <Collapsible
      className="px-4 py-2 space-y-2 border border-border rounded-md"
      onOpenChange={(e) => !loading && setOpen(e)}
      open={open}
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between cursor-pointer">
          <h4 className="text-sm font-semibold text-primary">
            HubSpot Company
          </h4>
          <Button
            className="p-0 w-9"
            disabled={loading}
            size="sm"
            type="button"
            variant="ghost"
          >
            {loading ? (
              <Icons.spinner className="w-4 h-4 mr-0" />
            ) : open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="sr-only">Toggle</span>
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2">{content}</CollapsibleContent>
    </Collapsible>
  )
}

export default OrganizationHubspotCompany
